/* eslint-disable react/no-array-index-key */
import NoChartDataComponent from '@Components/common/Charts/NoChartDataComponent';
import Skeleton from '@Components/RadixComponents/Skeleton';
import ToolTip from '@Components/RadixComponents/ToolTip';
import { cn } from '@Utils/index';
import isEmpty from '@Utils/isEmpty';
import { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ColumnSort } from '@tanstack/react-table';
import Icon from '@Components/common/Icon';
import ReportFilterPopOver from './ProgramFilterPopover/index';

interface IColumnItem {
  header: string;
  accessorKey: string;
  headerId?: string;
  className?: string;
  // eslint-disable-next-line no-unused-vars
  cell?: (rowData: Record<string, any>) => ReactNode;
}

interface INestedDataTable {
  className?: string;
  tableStyle?: Record<string, any>;
  data: Record<string, any>;
  columns: IColumnItem[];
  mainDataKey: string;
  nestingDataKey: string;
  isLoading: boolean;
  needSorting?: boolean;
  isFilterActive?: boolean;
  sortingState?: ColumnSort;
  // eslint-disable-next-line no-unused-vars
  onSorting?: (sorting: ColumnSort) => void;
  isProjectTable?: boolean; // Note: this prop only for DVS if you want to pass project data
  showExternalLink?: boolean; // whether show or hide external link icon
}

const detailRootPath = `/explore-by/programmes/`;

let clickCount = 0;

function getSortIconClassName(predicate: boolean) {
  return `naxatw-text-secondary-500 active-col ${predicate ? '!naxatw-hidden' : 'naxatw-visible'}`;
}

const excludeColumnNames = ['project_status'];

const NestedDataTable = ({
  className,
  tableStyle,
  columns,
  data,
  mainDataKey,
  nestingDataKey = 'data',
  isLoading,
  needSorting = true,
  isFilterActive = false,
  sortingState,
  onSorting,
  isProjectTable = false,
  showExternalLink = true,
}: INestedDataTable) => {
  const navigate = useNavigate();
  const { id: sortId, desc } = sortingState || {};
  const [searchFilterParams, setSearchFilterParams] = useSearchParams();

  // change sorting icon direction
  function handleSortingChange(column: IColumnItem) {
    if (!onSorting) return;

    if (sortId !== column.accessorKey) {
      clickCount = 0;
    }

    // reset sorting
    if (clickCount === 2) {
      clickCount = 0;
      onSorting({ id: '', desc: false });
      return;
    }

    if (sortId === column?.accessorKey) {
      onSorting({ id: sortId, desc: !desc });
    } else {
      onSorting({ id: column.accessorKey, desc: false });
    }
    clickCount += 1;
  }

  if (isLoading)
    return (
      <div className="naxatw-h-full naxatw-w-full naxatw-px-2 naxatw-py-2">
        <table className="naxatw-w-full">
          <tbody>
            {[...new Array(10)].map((_, i) => (
              <tr key={i} style={{ background: 'none' }}>
                {[...new Array(5)].map((__, index) => (
                  <td key={index}>
                    <Skeleton className="naxatw-h-16 naxatw-rounded-none" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

  if (isEmpty(data))
    return (
      <NoChartDataComponent className="naxatw-flex naxatw-min-h-[60vh] naxatw-items-center naxatw-justify-center naxatw-overflow-hidden max-lg:naxatw-w-screen max-md:naxatw-min-h-[30vh]" />
    );

  return (
    <table
      className={`naxatw-w-full !naxatw-text-lg ${className}`}
      style={tableStyle}
    >
      <thead
        className="naxatw-sticky naxatw-top-0 naxatw-z-10 naxatw-border-t naxatw-bg-gray-100"
        style={{ boxShadow: '0 0 0 .5px #e3e3e3' }}
      >
        <tr className="naxatw-h-10 naxatw-border-b naxatw-text-left">
          {columns?.map((column: IColumnItem) => {
            const showExpendMoreIcon =
              sortId === column?.accessorKey
                ? getSortIconClassName(!desc!!)
                : null;

            const showExpendLessIcon =
              sortId === column?.accessorKey
                ? getSortIconClassName(desc!!)
                : null;

            return (
              <th
                key={column.accessorKey}
                className={cn('naxatw-whitespace-nowrap naxatw-px-4')}
                onClick={() => {
                  // eslint-disable-next-line no-unused-expressions
                  !isFilterActive && handleSortingChange(column);
                }}
              >
                <div
                  className={cn(
                    `t-head naxatw-flex naxatw-cursor-pointer naxatw-items-center naxatw-justify-start naxatw-gap-1 !naxatw-text-sm hover:naxatw-text-secondary-500 ${sortId === column.accessorKey ? 'naxatw-text-secondary-500' : ''}`,
                    column.className,
                  )}
                >
                  <span className="naxatw-whitespace-nowrap !naxatw-text-sm naxatw-font-medium">
                    {column.header}
                  </span>
                  <span
                    className={`naxatw-flex naxatw-flex-col naxatw-items-center naxatw-justify-center naxatw-gap-1 ${needSorting && column?.header ? 'naxatw-block' : 'naxatw-hidden'}`}
                  >
                    {isFilterActive &&
                    !excludeColumnNames.includes(column?.headerId as string) ? (
                      <ReportFilterPopOver
                        onClick={() => {
                          searchFilterParams.set(
                            'filtercategory',
                            column?.headerId || column?.accessorKey || '',
                          );
                          setSearchFilterParams(searchFilterParams);
                        }}
                        headerId={column?.headerId || column?.accessorKey || ''}
                      />
                    ) : (
                      <>
                        <Icon
                          name="expand_less"
                          className={`!naxatw-flex !naxatw-h-[6px] !naxatw-items-center !naxatw-justify-start !naxatw-text-base group-hover:naxatw-text-secondary-500 ${
                            showExpendLessIcon
                          } naxatw-text-matt-100`}
                        />
                        <Icon
                          name="expand_more"
                          className={`!naxatw-text-icon-sm !naxatw-flex !naxatw-h-[6px] !naxatw-items-center !naxatw-justify-start !naxatw-text-base group-hover:naxatw-text-secondary-500 ${
                            showExpendMoreIcon
                          } naxatw-text-matt-100`}
                        />
                      </>
                    )}
                  </span>
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className="naxatw-text-sm 2xl:naxatw-text-[0.938rem]">
        {data?.map((tableData: Record<string, any>) => (
          <>
            <tr
              className="naxatw-w-full naxatw-border-b !naxatw-bg-none"
              key={tableData.id}
              style={{ background: 'none' }}
            >
              <td
                className="naxatw-break-words naxatw-border-r naxatw-px-4 naxatw-py-2 naxatw-font-medium"
                rowSpan={tableData?.[nestingDataKey]?.length}
              >
                {tableData?.[mainDataKey]}
                {/* this is only for DVS case to add link after the data of main data key */}
                {(!tableData?.program_id ? false : showExternalLink) && (
                  <Link
                    className="!naxatw-relative"
                    to={`${isProjectTable ? `?componentId=${tableData?.program_id}` : `${detailRootPath}${tableData?.program_id}/`}`}
                  >
                    <ToolTip
                      name="open_in_new"
                      message={`${isProjectTable ? 'Project' : 'Programme'} Details`}
                      className="naxatw-ml-1 naxatw-rounded-full naxatw-p-1 naxatw-text-[0.938rem] naxatw-text-primary-600 naxatw-duration-300 hover:naxatw-bg-primary-200"
                      triggerClassName="!naxatw-absolute !naxatw-top-[-px]"
                      iconClick={() => {
                        // stop page reload
                        if (isProjectTable) {
                          navigate(`./?componentId=${tableData?.program_id}`);
                          window.scrollTo({ top: 0 });
                          return;
                        }
                        window.open(
                          `${detailRootPath}${tableData?.program_id}/`,
                          '_blank',
                        );
                      }}
                    />
                  </Link>
                )}
                {/* end */}
              </td>
              {columns.map(column => {
                if (column.accessorKey === mainDataKey) return null;
                const value =
                  tableData?.[nestingDataKey]?.[0]?.[column.accessorKey];
                if (column?.cell) {
                  return (
                    <td
                      key={`${column.accessorKey}-${tableData.id}`}
                      className="naxatw-px-4 naxatw-py-2"
                    >
                      {column?.cell?.({
                        parentRowData: tableData,
                        childRowData: tableData?.[nestingDataKey]?.[0],
                      })}{' '}
                    </td>
                  );
                }
                return (
                  <td
                    className="naxatw-px-4 naxatw-py-2"
                    key={`${tableData.id}-${column.accessorKey}`}
                  >
                    {Array.isArray(value) ? (
                      <div className="naxatw-flex naxatw-flex-col naxatw-gap-1">
                        {isEmpty(value)
                          ? '-'
                          : value?.map((valuex, index) => (
                              <div
                                key={index}
                                className="naxatw-flex naxatw-gap-1 naxatw-break-words"
                              >
                                <div>-</div>
                                <div>{valuex}</div>
                              </div>
                            ))}
                      </div>
                    ) : (
                      value || '-'
                    )}
                  </td>
                );
              })}
            </tr>

            {tableData?.[nestingDataKey]
              ?.slice(1)
              ?.map((remainingRows: Record<string, any>) => (
                <tr
                  key={remainingRows.id}
                  className="naxatw-border-b !naxatw-bg-none"
                  style={{ background: 'none' }}
                >
                  {columns?.map(column => {
                    if (column.accessorKey === mainDataKey) return null;
                    const value = remainingRows?.[column.accessorKey];
                    if (column?.cell) {
                      return (
                        <td
                          key={`${column.accessorKey}-${remainingRows.id}`}
                          className="naxatw-px-4 naxatw-py-2"
                        >
                          {column?.cell?.({
                            parentRowData: tableData,
                            childRowData: remainingRows,
                          })}
                        </td>
                      );
                    }
                    return (
                      <td
                        className="naxatw-px-4 naxatw-py-2"
                        key={`${column.accessorKey}-${remainingRows.id}`}
                      >
                        {Array.isArray(value) ? (
                          <div className="naxatw-flex naxatw-flex-col naxatw-gap-1">
                            {value?.map(valuex => (
                              <div
                                key={valuex}
                                className="naxatw-flex naxatw-gap-1"
                              >
                                <div>-</div>
                                <div>{valuex}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          remainingRows?.[column.accessorKey]
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
          </>
        ))}
      </tbody>
    </table>
  );
};

export default NestedDataTable;
